import React, { useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { Cookie, Plus, X, Loader2, Check, AlertTriangle, XCircle } from 'lucide-react';
import toast from 'react-hot-toast';
import axiosInstance from '../../utils/axiosConfig';
import { formatoMonto, resumenRefrigerio } from '../../utils/menuComida';

// Armar el pedido de un refrigerio (Bandeja Procura): por cada ítem que pidió la
// persona, Procura elige combo(s) del menú y cuántos. Muestra en vivo unidades,
// unidades por persona, subtotal y total. El backend toma el precio del combo del
// menú al guardar (queda fijo aunque el menú cambie después).

const INPUT = 'w-full px-3 py-2 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-gray-50 focus:border-sura-blue';

const unidadesTexto = (n) => `${n.toLocaleString('es-VE')} und`;
const porPersona = (unidades, personas) =>
    personas ? (unidades / personas).toLocaleString('es-VE', { maximumFractionDigits: 1 }) : null;

// Filas iniciales por ítem: las del pedido ya armado (si su combo sigue en el
// menú) o, si no hay, una vacía con el primer combo del menú.
const filasIniciales = (items, pedido, combosDe) => {
    const filas = {};
    items.forEach(item => {
        const guardadas = (pedido?.lineas || []).filter(l =>
            l.item === item && combosDe(item).some(c => c.unidades === l.unidades));
        filas[item] = guardadas.length
            ? guardadas.map(l => ({ unidades: String(l.unidades), cantidad: String(l.cantidad) }))
            : [{ unidades: combosDe(item)[0] ? String(combosDe(item)[0].unidades) : '', cantidad: '' }];
    });
    return filas;
};

const ArmarRefrigerio = ({ sol, onClose, onGuardado }) => {
    const refri = sol.refrigerio || {};
    const items = useMemo(() => sol.refrigerio?.items || [], [sol.refrigerio]);
    const [menu, setMenu] = useState(null); // items de refrigerio del menú (con ocultos)
    const [filas, setFilas] = useState({});
    const [guardando, setGuardando] = useState(false);

    const combosDe = useMemo(() => {
        const porNombre = {};
        (menu || []).forEach(i => { porNombre[i.nombre.toLowerCase()] = i.combos || []; });
        return (item) => porNombre[item.toLowerCase()] || [];
    }, [menu]);

    useEffect(() => {
        let vigente = true;
        axiosInstance.get('/api/comidas/menu?todos=1')
            .then(res => { if (vigente) setMenu(res.data.filter(i => i.tipo === 'refrigerio')); })
            .catch(() => { if (vigente) { toast.error('No se pudo cargar el menú de refrigerios'); setMenu([]); } });
        return () => { vigente = false; };
    }, []);

    useEffect(() => {
        if (menu) setFilas(filasIniciales(items, refri.pedido, combosDe));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [menu]);

    useEffect(() => {
        const alEscape = (e) => { if (e.key === 'Escape' && !guardando) onClose(); };
        window.addEventListener('keydown', alEscape);
        return () => window.removeEventListener('keydown', alEscape);
    }, [onClose, guardando]);

    const cambiar = (item, i, campo, valor) =>
        setFilas(prev => ({ ...prev, [item]: prev[item].map((f, j) => (j === i ? { ...f, [campo]: valor } : f)) }));

    const agregarFila = (item) => {
        const usados = new Set(filas[item].map(f => f.unidades));
        const libre = combosDe(item).find(c => !usados.has(String(c.unidades)));
        if (!libre) return;
        setFilas(prev => ({ ...prev, [item]: [...prev[item], { unidades: String(libre.unidades), cantidad: '' }] }));
    };

    const quitarFila = (item, i) =>
        setFilas(prev => ({ ...prev, [item]: prev[item].filter((_, j) => j !== i) }));

    // Cálculo en vivo por ítem: unidades y subtotal (precio del combo del menú de hoy)
    const calculo = useMemo(() => {
        const r = { porItem: {}, total: 0, lineas: [] };
        items.forEach(item => {
            let unidades = 0;
            let subtotal = 0;
            (filas[item] || []).forEach(f => {
                const cantidad = Number(f.cantidad);
                const combo = combosDe(item).find(c => String(c.unidades) === f.unidades);
                if (!combo || !Number.isInteger(cantidad) || cantidad <= 0) return;
                unidades += cantidad * combo.unidades;
                subtotal += cantidad * Number(combo.precio || 0);
                r.lineas.push({ item, unidades: combo.unidades, cantidad });
            });
            r.porItem[item] = { unidades, subtotal };
            r.total += subtotal;
        });
        return r;
    }, [items, filas, combosDe]);

    const cantidadInvalida = items.some(item => (filas[item] || []).some(f => {
        if (f.cantidad === '') return false;
        const n = Number(f.cantidad);
        return !Number.isInteger(n) || n < 0 || n > 999;
    }));

    const guardar = async () => {
        if (cantidadInvalida) return toast.error('Las cantidades deben ser números enteros entre 0 y 999');
        if (calculo.lineas.length === 0) return toast.error('Indica al menos un combo con cantidad');
        setGuardando(true);
        try {
            const res = await axiosInstance.put(`/api/comidas/${sol.id}/refrigerio`, { lineas: calculo.lineas });
            toast.success('Pedido del refrigerio guardado');
            onGuardado(res.data);
        } catch (err) {
            toast.error(err.response?.data?.error || 'No se pudo guardar el pedido');
        } finally {
            setGuardando(false);
        }
    };

    return (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-gray-900/40 backdrop-blur-sm">
            <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.95 }}
                role="dialog" aria-modal="true" aria-labelledby="armar-refrigerio-titulo"
                className="bg-white rounded-3xl shadow-2xl w-full max-w-2xl overflow-hidden flex flex-col max-h-[90vh]">
                <div className="p-6 border-b border-gray-100 flex items-start justify-between gap-4 bg-gray-50">
                    <div className="min-w-0">
                        <h3 id="armar-refrigerio-titulo" className="text-lg font-bold text-gray-900 flex items-center gap-2">
                            <Cookie size={20} className="text-rose-500" /> {refri.armado ? 'Editar pedido' : 'Armar pedido'}
                        </h3>
                        <p className="text-sm text-sura-blue truncate">{refri.motivo}</p>
                        <p className="text-xs text-gray-500 mt-1">
                            {resumenRefrigerio(refri)} · {String(sol.fecha_consumo || '').split('-').reverse().join('/')} · {sol.solicitante_nombre}
                        </p>
                    </div>
                    <button onClick={onClose} disabled={guardando} aria-label="Cerrar"
                        className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-200 rounded-full transition-colors">
                        <XCircle size={24} />
                    </button>
                </div>

                <div className="p-6 overflow-y-auto space-y-4">
                    {menu === null ? (
                        <div className="flex justify-center py-10"><Loader2 size={24} className="animate-spin text-gray-300" /></div>
                    ) : items.map(item => {
                        const combos = combosDe(item);
                        const { unidades, subtotal } = calculo.porItem[item] || { unidades: 0, subtotal: 0 };
                        const filasItem = filas[item] || [];
                        const usados = new Set(filasItem.map(f => f.unidades));
                        return (
                            <div key={item} className="rounded-2xl border border-gray-100 p-4 space-y-3">
                                <div className="flex flex-wrap items-baseline justify-between gap-2">
                                    <h4 className="text-sm font-bold text-gray-900">{item}</h4>
                                    {unidades > 0 && (
                                        <p className="text-xs text-gray-500">
                                            <span className="font-semibold text-gray-800">{unidadesTexto(unidades)}</span>
                                            {refri.personas ? ` · ≈ ${porPersona(unidades, refri.personas)} por persona` : ''}
                                            {' · '}<span className="font-semibold text-gray-800 tabular-nums">{formatoMonto(subtotal)}</span>
                                        </p>
                                    )}
                                </div>

                                {combos.length === 0 ? (
                                    <p className="flex items-start gap-2 text-xs text-amber-700 bg-amber-50 border border-amber-200 px-3 py-2 rounded-lg">
                                        <AlertTriangle size={14} className="shrink-0 mt-0.5" />
                                        Este refrigerio no tiene combos. Agrega sus combos en la pestaña Menú para poder pedirlo.
                                    </p>
                                ) : (
                                    <>
                                        {filasItem.map((f, i) => {
                                            const combo = combos.find(c => String(c.unidades) === f.unidades);
                                            const n = Number(f.cantidad);
                                            const totalFila = combo && Number.isInteger(n) && n > 0 ? n * combo.unidades : 0;
                                            return (
                                                <div key={i} className="flex flex-wrap items-center gap-2">
                                                    <select value={f.unidades} onChange={e => cambiar(item, i, 'unidades', e.target.value)}
                                                        aria-label={`Combo de ${item}`} className={`${INPUT} w-44`}>
                                                        {combos.map(c => (
                                                            <option key={c.unidades} value={String(c.unidades)}
                                                                disabled={usados.has(String(c.unidades)) && String(c.unidades) !== f.unidades}>
                                                                {`Combo ${c.unidades} und · ${formatoMonto(c.precio)}`}
                                                            </option>
                                                        ))}
                                                    </select>
                                                    <span className="text-sm text-gray-400">×</span>
                                                    <input type="number" inputMode="numeric" min="0" max="999" step="1"
                                                        value={f.cantidad} onChange={e => cambiar(item, i, 'cantidad', e.target.value)}
                                                        placeholder="0" aria-label={`Cantidad de combos de ${item}`} className={`${INPUT} w-20`} />
                                                    <span className="text-sm text-gray-500 tabular-nums min-w-[5rem]">
                                                        {totalFila > 0 ? `= ${unidadesTexto(totalFila)}` : ''}
                                                    </span>
                                                    {filasItem.length > 1 && (
                                                        <button type="button" onClick={() => quitarFila(item, i)} title="Quitar combo"
                                                            className="p-1.5 hover:bg-gray-100 rounded-lg text-gray-400 hover:text-red-500 transition-colors">
                                                            <X size={14} />
                                                        </button>
                                                    )}
                                                </div>
                                            );
                                        })}
                                        {filasItem.length < combos.length && (
                                            <button type="button" onClick={() => agregarFila(item)}
                                                className="text-xs text-sura-blue hover:underline flex items-center gap-1">
                                                <Plus size={12} /> Otro combo
                                            </button>
                                        )}
                                    </>
                                )}
                            </div>
                        );
                    })}
                    {menu !== null && (
                        <p className="text-xs text-gray-400">Un refrigerio en 0 no entra en el pedido. Los precios son los del menú de hoy y quedan fijos al guardar.</p>
                    )}
                </div>

                <div className="p-6 border-t border-gray-100 bg-gray-50 flex flex-wrap items-center justify-between gap-3">
                    <div>
                        <p className="text-[11px] text-gray-500 uppercase tracking-wider font-semibold">Total del pedido</p>
                        <p className="text-xl font-bold text-gray-900 tabular-nums">{formatoMonto(calculo.total)}</p>
                    </div>
                    <div className="flex gap-3">
                        <button onClick={onClose} disabled={guardando}
                            className="px-5 py-2.5 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-xl text-sm font-medium transition-colors">
                            Cancelar
                        </button>
                        <button onClick={guardar} disabled={guardando || menu === null || calculo.lineas.length === 0}
                            className="px-5 py-2.5 text-white bg-sura-blue hover:bg-sura-blue-dark rounded-xl text-sm font-medium transition-colors flex items-center gap-2 disabled:opacity-50">
                            {guardando ? <Loader2 size={16} className="animate-spin" /> : <Check size={16} />}
                            Guardar pedido
                        </button>
                    </div>
                </div>
            </motion.div>
        </div>
    );
};

export default ArmarRefrigerio;
